import React, { useState } from "react";
import { Button, Input, Modal, Spin, message } from "antd";
import {
  CaretDownOutlined,
  CaretRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FormOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { getAppSettings, updateAppSettings } from "../../utils/API";

// Outlook's own folder pane, replicated so the Email tab reads like the
// desktop client the sales team already lives in every day. Linked history
// and Needs confirm live here (they used to be tabs above the content).
// Inbox moved up top next to the Customer Email / Dealer Email tabs, so it
// isn't repeated here.

export interface EmailFolderSidebarProps {
  /** The connected mailbox address, shown as the account header. */
  connectedUser: string;
  /** True while the Sent Mail folder is the one on screen. */
  sentActive: boolean;
  onSelectSent: () => void;
  /** True while the Linked history view is the one on screen. */
  historyActive: boolean;
  onSelectHistory: () => void;
  /** True while the Needs confirm view is the one on screen. */
  pendingActive: boolean;
  onSelectPending: () => void;
}

const EmailFolderSidebar: React.FC<EmailFolderSidebarProps> = ({
  connectedUser,
  sentActive,
  onSelectSent,
  historyActive,
  onSelectHistory,
  pendingActive,
  onSelectPending,
}) => {
  const [accountOpen, setAccountOpen] = useState(true);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signature, setSignature] = useState("");
  const [initialSignature, setInitialSignature] = useState("");
  const [loadingSignature, setLoadingSignature] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);

  const handleOpenSignature = async () => {
    setSignatureModalOpen(true);
    setLoadingSignature(true);
    try {
      const settings = await getAppSettings();
      const sig = String(settings?.emailSignature || "");
      setSignature(sig);
      setInitialSignature(sig);
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Failed to load signature");
    } finally {
      setLoadingSignature(false);
    }
  };

  const handleSaveSignature = async () => {
    setSavingSignature(true);
    try {
      await updateAppSettings({ emailSignature: signature });
      message.success("Signature saved");
      setInitialSignature(signature);
      setSignatureModalOpen(false);
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Failed to save signature");
    } finally {
      setSavingSignature(false);
    }
  };

  const folderRowClass = (active: boolean) =>
    [
      "flex items-center gap-2 rounded px-2 py-1 cursor-pointer",
      active
        ? "bg-[#e8f0fe] font-semibold text-[#004aad]"
        : "text-gray-700 hover:bg-gray-100",
    ].join(" ");

  return (
    <div className="flex h-full w-56 shrink-0 flex-col gap-3 overflow-y-auto rounded-lg border border-gray-200 bg-white px-2 py-3 text-sm">
      {/* The account itself, with the views the page offers that aren't up
          top — Linked history and Needs confirm are the portal's own
          record/queue views; Sent Mail is the other mailbox folder. */}
      <div>
        <button
          type="button"
          onClick={() => setAccountOpen((open) => !open)}
          className="flex w-full items-center gap-1 px-2 py-1 text-left font-medium text-gray-700"
        >
          {accountOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
          <span className="truncate">{connectedUser || "mailbox"}</span>
        </button>

        {accountOpen ? (
          <div className="ml-1 flex flex-col gap-0.5">
            <div
              className={folderRowClass(historyActive)}
              onClick={onSelectHistory}
            >
              <CheckCircleOutlined />
              <span className="flex-1 truncate">Linked history</span>
            </div>

            <div
              className={folderRowClass(pendingActive)}
              onClick={onSelectPending}
            >
              <ClockCircleOutlined />
              <span className="flex-1 truncate">Needs confirm</span>
            </div>

            <div className={folderRowClass(sentActive)} onClick={onSelectSent}>
              <SendOutlined />
              <span className="flex-1 truncate">Sent Mail</span>
            </div>

            <div
              className={folderRowClass(signatureModalOpen)}
              onClick={handleOpenSignature}
            >
              <FormOutlined />
              <span className="flex-1 truncate">Reply Signature</span>
            </div>
          </div>
        ) : null}
      </div>

      <Modal
        open={signatureModalOpen}
        onCancel={() => setSignatureModalOpen(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <div className="p-1">
          <div className="mb-3">
            <h3 className="text-base font-semibold text-gray-800 m-0">Reply Signature</h3>
          </div>

          {loadingSignature ? (
            <div className="flex justify-center py-12">
              <Spin />
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Input.TextArea
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                rows={16}
                placeholder={"Regards,\nAlliance Test Equipment"}
                className="!text-sm !font-sans !rounded-lg"
                style={{ minHeight: 340 }}
              />
              <div className="flex justify-end">
                <Button
                  type="primary"
                  loading={savingSignature}
                  disabled={loadingSignature || signature === initialSignature}
                  onClick={handleSaveSignature}
                  className="!bg-[#004aad] !border-[#004aad] !text-white hover:!bg-[#003a87] hover:!border-[#003a87] disabled:!bg-gray-200 disabled:!border-gray-200 disabled:!text-gray-400"
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default EmailFolderSidebar;
